import React, { useState, useEffect } from 'react';
import { FileUp, Download, CheckCircle, Phone, Mail, MapPin, Clock, FileText, Send, Lock, Edit, Award, Users, FileCheck } from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schemaData';
import ScrollIndicator from '../components/ScrollIndicator';
import FormNDIS from './FormNDIS';
import './Refer.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const Refer = () => {
  // Breadcrumb Schema
  const breadcrumbs = breadcrumbSchema([
    { name: "Home", url: "https://www.clinicalminds.net/" },
    { name: "Refer", url: "https://www.clinicalminds.net/refer" }
  ]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [showNDISModal, setShowNDISModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [formData, setFormData] = useState({
    patientName: '',
    referringPractitioner: '',
    contactEmail: '',
    file: null
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      file: e.target.files[0]
    }));
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setUploading(true);
    setUploadError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('patient_name', formData.patientName);
      formDataToSend.append('referring_practitioner', formData.referringPractitioner);
      formDataToSend.append('contact_email', formData.contactEmail);
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/referral/upload`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUploadSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setShowUploadModal(false);
          setUploadSuccess(false);
          setFormData({
            patientName: '',
            referringPractitioner: '',
            contactEmail: '',
            file: null
          });
        }, 3000);
      } else {
        setUploadError(data.detail || 'Upload failed. Please try again.');
      }
    } catch (error) {
      setUploadError('Network error. Please check your connection and try again.');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page-refer">
      <SEO 
        title="GP Referrals | Healthcare Professional Referrals | Clinical Minds Psychology"
        description="Streamlined referral process for GPs and allied health professionals. Upload Mental Health Treatment Plans securely. Evidence-based psychology services with Medicare rebates available for eligible clients and collaborative care."
        canonicalUrl="https://www.clinicalminds.net/refer"
        schemaData={breadcrumbs}
      />
      <ScrollIndicator />
      
      {/* Hero Section */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Referrers</span>
          <h1 className="heading-hero">Healthcare Professional Referrals</h1>
          <p className="hero-subline">Streamlined referral process for GPs and allied health professionals</p>
        </div>
      </section>

      {/* Content Section with cream background */}
      <div className="refer-content">
        {/* Quick Referral Upload Section */}
        <section className="quick-referral-section">
          <div className="referral-cards-grid">
            <div className="quick-referral-card card">
              <FileUp size={48} strokeWidth={1.5} className="referral-icon" />
              <h2 className="heading-3">GP or Allied Health Referral</h2>
              <p className="body-medium">Upload your patient's referral documentation securely</p>
              <button 
                onClick={() => setShowUploadModal(true)}
                className="btn btn-primary"
              >
                <FileUp size={20} strokeWidth={1.5} />
                Upload Referral
              </button>
            </div>

            <div className="quick-referral-card card">
              <FileText size={48} strokeWidth={1.5} className="referral-icon" />
              <h2 className="heading-3">NDIS Referral</h2>
              <p className="body-medium">Quick referral for therapy supports or assessments. We will contact you within 24 hours</p>
              <button 
                onClick={() => setShowNDISModal(true)}
                className="btn btn-primary"
              >
                <FileText size={20} strokeWidth={1.5} />
                NDIS Referral Form
              </button>
            </div>
          </div>
        </section>

        {/* Why Refer Section */}
        <section className="why-refer-section">
          <div className="section-header card">
            <h2 className="heading-1">Why Refer to Clinical Minds Psychology?</h2>
            <p className="body-medium">We provide comprehensive, evidence-based psychological services with a focus on collaborative care</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-item card">
              <Award size={32} strokeWidth={1.5} className="benefit-icon" />
              <h3 className="heading-3">Evidence-Based Approaches</h3>
              <p className="body-medium">We use evidence based therapeutic interventions tailored to each client's needs</p>
            </div>
            <div className="benefit-item card">
              <Clock size={32} strokeWidth={1.5} className="benefit-icon" />
              <h3 className="heading-3">Timely Appointments</h3>
              <p className="body-medium">We do our best to see your patients as soon as we can, with flexible scheduling options</p>
            </div>
            <div className="benefit-item card">
              <Users size={32} strokeWidth={1.5} className="benefit-icon" />
              <h3 className="heading-3">Collaborative Care</h3>
              <p className="body-medium">Regular updates and feedback to referring practitioners throughout treatment</p>
            </div>
            <div className="benefit-item card">
              <FileCheck size={32} strokeWidth={1.5} className="benefit-icon" />
              <h3 className="heading-3">Medicare Knowledge</h3>
              <p className="body-medium">Comprehensive understanding of Medicare rebates and Mental Health Treatment Plans</p>
            </div>
          </div>
        </section>

        {/* Referral Process Section */}
        <section className="referral-process-section">
          <div className="section-header card">
            <h2 className="heading-1">Referral Process</h2>
            <p className="body-medium">Simple steps to refer your patients to our practice</p>
            <p className="body-medium" style={{ marginTop: '1rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
              <strong>NB.</strong> The information below is for GP initiated mental health treatment plans. NDIS referrals only require the referral from above to be completed.
            </p>
          </div>
          <div className="process-steps">
            <div className="process-step card">
              <div className="step-number">1</div>
              <h3 className="heading-3">The Mental Health Treatment Plan</h3>
              <p className="body-medium">
                The Department of Health have updated their FAQs for preparation of a referral and Mental Health Treatment Plans and it can be found <a href="https://www.health.gov.au/resources/publications/frequently-asked-questions-better-access-to-psychiatrists-psychologists-and-general-practitioners-through-the-mbs-initiative?language=en" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue-primary)', textDecoration: 'underline' }}>here</a>.
                <br /><br />
                The Department of Health also have other useful Factsheets for:
                <br /><br />
                <a href="https://www.health.gov.au/resources/publications/better-access-fact-sheet-professionals?language=en" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue-primary)', textDecoration: 'underline' }}>GPs, Prescribed Medical Practitioners and other professionals</a>
                <br /><br />
                <a href="https://www.health.gov.au/resources/publications/better-access-fact-sheet-patients?language=en" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue-primary)', textDecoration: 'underline' }}>Patients</a>
              </p>
            </div>
            <div className="process-step card">
              <div className="step-number">2</div>
              <h3 className="heading-3">Submit Referral</h3>
              <p className="body-medium">
                1. You can also download a template for a Mental Health Treatment Plan from the Department of Health <a href="https://www.health.gov.au/resources/publications/better-access-gp-mental-health-treatment-plan-template?language=en" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue-primary)', textDecoration: 'underline' }}>here</a>.
                <br /><br />
                2. Send us your patient's referral documentation (Mental Health Treatment Plan if you are a medical doctor) via our secure <button onClick={() => setShowUploadModal(true)} style={{ background: 'none', border: 'none', color: 'var(--blue-primary)', textDecoration: 'none', fontWeight: 500, cursor: 'pointer', padding: 0, font: 'inherit' }}>referral link</button> or send via fax 02 6699 1457 / <a href="mailto:contact@clinicalminds.net" style={{ color: 'var(--blue-primary)', textDecoration: 'none', fontWeight: 500 }}>email</a>
              </p>
            </div>
            <div className="process-step card">
              <div className="step-number">3</div>
              <h3 className="heading-3">Patient Contact</h3>
              <p className="body-medium">We contact your patient within 24-48 hours to arrange an initial appointment. If they would like to follow us up, please direct them to our website <a href="https://www.clinicalminds.net" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue-primary)', textDecoration: 'underline' }}>www.clinicalminds.net</a> or our phone number <a href="tel:0266002710" style={{ color: 'var(--blue-primary)', textDecoration: 'underline' }}>02 6600 2710</a>.</p>
            </div>
            <div className="process-step card">
              <div className="step-number">4</div>
              <h3 className="heading-3">Forms</h3>
              <p className="body-medium">Your patient will be sent intake and consent forms directly by email, after the appointment is booked</p>
            </div>
            <div className="process-step card">
              <div className="step-number">5</div>
              <h3 className="heading-3">Initial Assessment</h3>
              <p className="body-medium">Comprehensive psychological assessment and treatment planning session</p>
            </div>
            <div className="process-step card">
              <div className="step-number">6</div>
              <h3 className="heading-3">Treatment Updates</h3>
              <p className="body-medium">Regular progress reports sent to referring practitioner as appropriate</p>
            </div>
          </div>
        </section>

        {/* Forms Section */}
        <section className="forms-section">
          <div className="forms-card card">
            <div className="forms-header">
              <FileText size={28} className="forms-icon" />
              <h2 className="heading-1">Essential Forms & Documents</h2>
            </div>
            <p className="forms-description body-medium">
              Access important forms and information about our practice
            </p>
            <div className="forms-grid">
              <a href="/Clinical_Minds_Privacy_Policy.pdf" target="_blank" rel="noopener noreferrer" className="form-link">
                <div className="form-item">
                  <Lock size={20} strokeWidth={1.5} className="form-icon" />
                  <div className="form-content">
                    <h3 className="heading-3">Privacy Policy</h3>
                    <p className="body-small">Review how we protect and handle your personal information</p>
                  </div>
                </div>
              </a>
              
              <a href="/Clinical_Minds_Terms_and_Conditions.pdf" target="_blank" rel="noopener noreferrer" className="form-link">
                <div className="form-item">
                  <FileText size={20} strokeWidth={1.5} className="form-icon" />
                  <div className="form-content">
                    <h3 className="heading-3">Terms and Conditions</h3>
                    <p className="body-small">Important terms for using our services and booking system</p>
                  </div>
                </div>
              </a>
              
              <a href="/Clinical_Minds_New_Client_Intake_and_Consent.pdf" target="_blank" rel="noopener noreferrer" className="form-link">
                <div className="form-item">
                  <Edit size={20} strokeWidth={1.5} className="form-icon" />
                  <div className="form-content">
                    <h3 className="heading-3">New Client Intake Form</h3>
                    <p className="body-small">Download if you can't fill the form out online</p>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Areas of Interest Section */}
        <section className="areas-section">
          <div className="section-header card">
            <h2 className="heading-1">Our Areas of Interest</h2>
            <p className="body-medium">We work with a wide range of mental health concerns and life challenges</p>
          </div>
          <div className="areas-grid">
            <div className="area-category card">
              <h3 className="heading-3">Mental Health</h3>
              <ul className="area-list">
                <li>Depression</li>
                <li>Anxiety and Social Anxiety</li>
                <li>Phobias</li>
                <li>Trauma and PTSD</li>
                <li>Bipolar Disorder</li>
                <li>Substance and Addiction</li>
                <li>ADHD & Neurodiversity</li>
                <li>Stress management</li>
                <li>Grief and loss</li>
              </ul>
            </div>
            <div className="area-category card">
              <h3 className="heading-3">Life Transitions</h3>
              <ul className="area-list">
                <li>Relationships</li>
                <li>Identity and self-esteem</li>
                <li>Work-related stress</li>
                <li>Life adjustments</li>
                <li>Chronic illness adaptation</li>
              </ul>
            </div>
            <div className="area-category card">
              <h3 className="heading-3">Age Groups</h3>
              <ul className="area-list">
                <li>Adolescents (13+)</li>
                <li>Young adults</li>
                <li>Adults</li>
                <li>Seniors</li>
                <li>Families</li>
              </ul>
            </div>
          </div>
          <div className="diagnosis-assessments-card card" style={{ padding: '2rem', textAlign: 'center', background: 'transparent', border: '1px solid rgba(107, 140, 174, 0.15)', borderRadius: '16px' }}>
            <h3 className="heading-3" style={{ marginBottom: '1rem', color: 'var(--blue-primary)' }}>Diagnosis and Psychological Assessments</h3>
            <p className="body-medium">We can conduct a wide range of assessments: cognitive, behavioural, clinical that inform treatment planning or for other purposes such as school support, NDIS or other government support. Assessments include but are not limited to those for ADHD, Neurodiversity, learning difficulties and disorders.</p>
          </div>
          <div className="medicare-note card">
            <p className="body-medium"><strong>Medicare Rebates Available:</strong> We provide services under Mental Health Treatment Plans with Medicare rebates for eligible clients.</p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="refer-contact-section">
          <div className="section-header card">
            <h2 className="heading-1">Contact for Referrals</h2>
            <p className="body-medium">Reach out to us for any questions about the referral process</p>
          </div>
          <div className="contact-grid">
            <div className="contact-item card">
              <Phone size={32} strokeWidth={1.5} className="contact-icon" />
              <h3 className="heading-3">Phone</h3>
              <div className="contact-details">
                <p><strong>Office:</strong> <a href="tel:0266002710" style={{ color: 'var(--blue-primary)', textDecoration: 'none', fontWeight: 500 }}>02 6600 2710</a></p>
                <p><strong>Mobile/SMS:</strong> <a href="tel:0415220944" style={{ color: 'var(--blue-primary)', textDecoration: 'none', fontWeight: 500 }}>0415 220 944</a></p>
                <p><strong>Fax:</strong> <a href="tel:0266991457" style={{ color: 'var(--blue-primary)', textDecoration: 'none', fontWeight: 500 }}>02 6699 1457</a></p>
              </div>
            </div>
            <div className="contact-item card">
              <Mail size={32} strokeWidth={1.5} className="contact-icon" />
              <h3 className="heading-3">Email</h3>
              <div className="contact-details">
                <p><a href="mailto:contact@clinicalminds.net" style={{ color: 'var(--blue-primary)', textDecoration: 'none', fontWeight: 500 }}>contact@clinicalminds.net</a></p>
              </div>
            </div>
            <div className="contact-item card">
              <Clock size={32} strokeWidth={1.5} className="contact-icon" />
              <h3 className="heading-3">Contact Hours</h3>
              <div className="contact-details">
                <p>Monday - Friday</p>
                <p>9:00 AM - 5:00 PM</p>
                <p>Saturday & Sunday: We will</p>
                <p>respond within 24 hours</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="heading-1">Upload Referral</h2>
              <button 
                className="modal-close-btn"
                onClick={() => setShowUploadModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            {uploadSuccess ? (
              <div className="upload-success">
                <CheckCircle size={64} className="success-icon" />
                <h3 className="heading-3">Referral Uploaded Successfully!</h3>
                <p className="body-medium">Thank you. We'll contact the patient within 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleFileUpload} className="modal-body-form">
                {formsOff && <PreviewFormNotice />}
                <div className="privacy-notice-top">
                  <p className="body-small" style={{ fontStyle: 'italic', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                    We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a>.
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="patientName" className="form-label">Patient Name *</label>
                  <input
                    type="text"
                    id="patientName"
                    name="patientName"
                    className="form-input"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    required
                    disabled={uploading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="referringPractitioner" className="form-label">Referring Practitioner *</label>
                  <input
                    type="text"
                    id="referringPractitioner"
                    name="referringPractitioner"
                    className="form-input"
                    value={formData.referringPractitioner}
                    onChange={handleInputChange}
                    required
                    disabled={uploading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactEmail" className="form-label">Contact Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    className="form-input"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    required
                    disabled={uploading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="file" className="form-label">Referral Document (Optional)</label>
                  <input
                    type="file"
                    id="file"
                    name="file"
                    className="form-input file-input"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={uploading}
                  />
                  <p className="body-small form-help">
                    Accepted formats: PDF, Word, or images (max 10MB)
                  </p>
                </div>

                {uploadError && (
                  <div className="upload-error">
                    <p className="body-medium">{uploadError}</p>
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn btn-primary btn-submit"
                  disabled={uploading || formsOff}
                >
                  {uploading ? (
                    <>
                      <span className="spinner"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit Referral
                    </>
                  )}
                </button>

                <p className="body-small consent-text">
                  By submitting this form, you confirm that you have read and agree to our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {/* NDIS Modal */}
      {showNDISModal && (
        <div className="modal-overlay" onClick={() => setShowNDISModal(false)}>
          <div className="modal-content ndis-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn" 
              onClick={() => setShowNDISModal(false)}
              aria-label="Close modal"
            >
              &times;
            </button>
            <div className="modal-body">
              <FormNDIS />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Refer;
